/** @jsxRuntime classic */
/** @jsx jsx */
import { Fragment, memo, ReactNode, useMemo, useState } from 'react';
import { jsx } from '@emotion/core';
import { ServerInfo } from 'src/orchd-client';
import { defineMessage, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { useGetServerDiskUsageQuery } from 'src/api_services/servers/query';
import { Box } from 'src/components/Box';
import { DonutPieChart } from 'src/components/Charts/DonutPieChart';
import { ArcMask } from 'src/components/Charts/DonutPieChart/DonutPieChartArcMask';
import { FailedToLoad } from 'src/components/FailedToLoad';
import { Icon } from 'src/components/Icon';
import { Loading } from 'src/components/Loading';
import { ServerStatusCard } from 'src/components/ServerStatusCard';
import { Text } from 'src/components/Text';
import { viewportSelectors } from 'src/containers/Viewport/_redux/selectors';
import theme from 'src/design-system/theme';
import { messages } from 'src/messages';
import { AppState } from 'src/store';
import { getUsageStat } from 'src/utils/graphs/graphs';
import { getByteString } from 'src/utils/helpers';
import { getSortedDiskUsage } from 'src/utils/servers/servers';

import { style } from '../ServerUsage.style';

type StateProps = ReturnType<typeof stateToProps>;

export interface Props extends StateProps {
  server: ServerInfo;
}

const t = {
  remaining: defineMessage({
    id: 'manage_server.remaining',
    defaultMessage: 'remaining',
  }),
};

export const _DiskUsageCard = ({ isTabletUp, server }: Props) => {
  const [collapsed, setIsCollapsed] = useState(true);
  const { id: serverId } = server;

  const { data: diskUsage, isLoading, isError } = useGetServerDiskUsageQuery({ serverId });

  const sortedDisks = useMemo(() => {
    return getSortedDiskUsage(diskUsage?.disks);
  }, [diskUsage]);

  const usageTotals = useMemo(() => {
    return sortedDisks.reduce(
      (accTotals, { usage }) => {
        const { used, total } = usage;
        return { used: accTotals.used + used, total: accTotals.total + total };
      },
      { used: 0, total: 0 }
    );
  }, [sortedDisks]);

  const displayData = useMemo(() => {
    return sortedDisks.reduce((acc, disk) => {
      const { mountPoint, usage } = disk;
      const { used, total } = usage;
      const percentage = used / total;
      const usedText = getByteString(used);
      const totalText = getByteString(total);

      const label = (
        <FormattedMessage
          {...messages.usage_tooltip}
          values={{
            mountPoint,
            usedText,
            totalText,
            percentage: (percentage * 100).toFixed(1),
            b: (bolded) => <b>{bolded}</b>,
          }}
        />
      );

      const usageColor = getUsageStat(percentage);

      acc.push({
        id: `${disk.name}-remaining`,
        label,
        color: theme.colors.grey5,
        value: total - used,
      });
      acc.push({
        id: `${disk.name}-used`,
        label,
        color: theme.colors[usageColor.color],
        value: used,
      });
      return acc;
    }, [] as { id: string; label: ReactNode; color: string; value: number }[]);
  }, [sortedDisks]);

  const highestUsage = useMemo(() => {
    const highestPercentage = sortedDisks.reduce((accPercentage, disk) => {
      const { usage } = disk;
      const { used, total } = usage;
      const currPercentage = used / total;
      return currPercentage > accPercentage ? currPercentage : accPercentage;
    }, 0);

    return getUsageStat(highestPercentage);
  }, [sortedDisks]);

  return (
    <ServerStatusCard
      fullHeight
      padding={{ xs: 'lg', sm: 'xl' }}
      borderTopColor={highestUsage.color === 'success' ? undefined : highestUsage.color}
    >
      <Box css={style.usageCardHeader} j="space-between" onClick={() => setIsCollapsed(!collapsed)} a="center">
        <span>
          {/* TODO: commented out */}
          {/* <StatusIcon css={style.usageStatusIcon} type={diskUsageError ? 'error' : usage.color} /> */}

          <Text size={{ xs: 'md', sm: 'lg' }}>
            <FormattedMessage id="manage_server.disk_usage" defaultMessage="Disk usage" />
          </Text>
        </span>
        {!isTabletUp && <Icon name={collapsed ? 'chevron down' : 'chevron up'} />}
      </Box>

      {(isTabletUp || !collapsed) && (
        <Box css={style.tallCardBody} p="lg" pt="0" position="relative" mt="xl">
          {isError && <FailedToLoad smallText fullHeight />}

          {isLoading && !isError && <Loading absoluteCenter />}

          {!!sortedDisks.length && !isLoading && !isError && (
            <Fragment>
              <DonutPieChart
                data-qa="chart"
                data={displayData}
                tooltip={(data) => (
                  <Box p="sm" bg="dark" w="160px">
                    <Text color="light" size="sm">
                      {data.datum.label}
                    </Text>
                  </Box>
                )}
                centerLabel={
                  <Box d="column" a="center">
                    <Text size="sm" align="center">
                      <FormattedMessage {...messages.usage} />
                    </Text>
                    <Text size="xl" align="center" weight="bold">
                      {getByteString(usageTotals.used)}
                    </Text>
                  </Box>
                }
                layers={['arcLinkLabels', 'arcs', 'arcLabels', 'legends', ArcMask]}
              />
              <Box j="center">
                <Text size="sm" weight="bold">
                  {getByteString(usageTotals.total - usageTotals.used)}&nbsp;
                </Text>
                <Text size="sm">{<FormattedMessage {...t.remaining} />}</Text>
              </Box>
            </Fragment>
          )}
        </Box>
      )}
    </ServerStatusCard>
  );
};

const stateToProps = (state: AppState) => ({
  isTabletUp: viewportSelectors.isTabletUp(state),
});

export const DiskUsageCard = connect(stateToProps)(memo(_DiskUsageCard));
